import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import projectsData from '../../components/projects/projects.json';
import { resolveImage, ResponsiveImage } from '../images/responsive-image';

/**
 * O case de decisão de um projeto. Só `provesKey` é obrigatório, e é o único que
 * aparece sem clique: é a frase que responde "o que esse trabalho prova", que é
 * o que o recrutador lê antes de decidir se abre o resto.
 *
 * Os cinco campos do detalhe são opcionais de propósito. A folha do vault de um
 * projeto pequeno não tem restrição nem evidência medida, e inventar as duas para
 * preencher a estrutura seria claim sem lastro.
 */
export interface ProjectDecision {
  provesKey: string;
  contextKey?: string;
  constraintKey?: string;
  decisionKey?: string;
  evidenceKey?: string;
  impactKey?: string;
  /** Número relatado pelo Augusto, sem medição anexada. A UI diz isso na cara. */
  selfReported?: boolean;
}

export type { ResponsiveImage };

interface RawProject {
  id: string;
  titleKey: string;
  descriptionKey: string;
  imageKey?: string;
  imageKeys?: string[];
  category: 'commercial' | 'study';
  tagsKeys: string[];
  technologies: string[];
  featured?: boolean;
  /**
   * A imagem é ilustração conceitual, não captura do sistema. Existe para os
   * cases sob confidencialidade e para os projetos sem interface alcançável:
   * sem isso, arte gerada na capa passaria por print de tela do cliente.
   */
  illustrated?: boolean;
  demoUrl?: string;
  githubUrl?: string;
  paperUrl?: string;
  decision?: ProjectDecision;
}

export interface ProjectItem extends RawProject {
  /** Capa do cartão; `null` em case sem captura publicável. */
  cover: ResponsiveImage | null;
  /** Galeria do modal: `imageKeys` quando existe, senão a própria capa. */
  gallery: ResponsiveImage[];
  /** Só as tecnologias que cabem no cartão, mais o resto contado. */
  visibleTechnologies: string[];
  hiddenTechnologies: number;
}

interface ProjectsData {
  commercial: RawProject[];
  study: RawProject[];
}

const CARD_TECHNOLOGIES = 4;

function hydrate(raw: RawProject): ProjectItem {
  const cover = resolveImage(raw.imageKey);
  const gallery = raw.imageKeys?.length
    ? raw.imageKeys.map(resolveImage).filter((image): image is ResponsiveImage => image !== null)
    : cover
      ? [cover]
      : [];

  return {
    ...raw,
    cover,
    gallery,
    visibleTechnologies: raw.technologies.slice(0, CARD_TECHNOLOGIES),
    hiddenTechnologies: Math.max(0, raw.technologies.length - CARD_TECHNOLOGIES)
  };
}

/** Fonte única dos projetos para cards, terminal e quick open. */
@Injectable({ providedIn: 'root' })
export class ProjectCatalogService {
  readonly commercial = ((projectsData as ProjectsData).commercial || []).map(hydrate);
  readonly study = ((projectsData as ProjectsData).study || []).map(hydrate);
  readonly all = [...this.commercial, ...this.study];
  /** Recorte curto da seção de projetos; o inventário fica atrás de "ver todos". */
  readonly featured = this.all.filter((project) => project.featured);

  find(slug: string): ProjectItem | undefined {
    return this.all.find((project) => project.id === slug);
  }

  /**
   * As tecnologias mais frequentes do inventário, para a barra de filtro.
   * Ordena por número de projetos e desempata em ordem alfabética, para a barra
   * não trocar de ordem entre builds. O rótulo exibido é a primeira grafia
   * encontrada no catálogo — `Node.js` e `node.js` contam como a mesma coisa.
   */
  topTechnologies(limit: number): string[] {
    const counts = new Map<string, { label: string; count: number }>();

    for (const project of this.all) {
      for (const technology of project.technologies) {
        const key = technology.toLowerCase();
        const entry = counts.get(key);
        if (entry) entry.count += 1;
        else counts.set(key, { label: technology, count: 1 });
      }
    }

    return [...counts.values()]
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
      .slice(0, limit)
      .map((entry) => entry.label);
  }

  search(query: string, translate: TranslateService): ProjectItem[] {
    const normalized = this.normalize(query);
    if (!normalized) return this.all;

    return this.all.filter((project) => {
      const searchable = [
        project.id,
        translate.instant(project.titleKey),
        translate.instant(project.descriptionKey),
        ...project.technologies,
        ...project.tagsKeys.map((key) => translate.instant(key))
      ]
        .map((value) => this.normalize(value))
        .join(' ');

      return normalized.split(' ').every((term) => searchable.includes(term));
    });
  }

  private normalize(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase()
      .trim();
  }
}
